import { uniqueId } from 'lodash'
import React, { FC, useMemo, useState } from 'react'
import { format, nowDate, secToHourMinute } from '../../../utils/date'
import LoadingSquare from '../common/LoadingSquare'
import Tips from '../common/Tips'

interface Props {
  list: {
    scheduled: number
    label: string
    values: {
      name: string
      value: number
      color?: string
    }[]
    color?: string
  }[]
  chartSize: number
  color?: string
  strongColor?: string
  type?: string
  gap?: boolean
  horizontal?: boolean
  ruler?: boolean
  scale?: boolean
  labelSkip?: number
  vLabelLimit?: number
  legend?: boolean
  tips?: boolean
  clickCallback: (label: string) => void
  selectLabel?: string
  max: number
  isGraphLoading: boolean
  round?: boolean
}

const DetailScheduleChart: FC<Props> = (props) => {
  const {
    list,
    chartSize,
    color,
    strongColor,
    type,
    gap,
    horizontal,
    ruler,
    scale,
    labelSkip,
    vLabelLimit,
    legend,
    tips,
    clickCallback,
    selectLabel,
    max,
    isGraphLoading,
    round,
  } = props

  const V_LABEL_LIMIT = 3

  const [focusLabel, setFocusLabel] = useState<string | null>(null)

  const maxValue = useMemo(() => {
    const tmpMax = Math.max(...list.map((v) => v.values.reduce((s, w) => s + w.value, 0)), max)
    const tmpAry = Math.floor(tmpMax / 3600).toString().split('')
    if (tmpAry.length < 3) {
      return 100 * 3600
    }
    if (Math.floor(tmpMax / 3600) % Number(`1${"0".repeat(tmpAry.length - 1)}`) === 0) {
      return tmpMax
    }
    if (tmpAry[0] === "9") {
      return Number(`1${"0".repeat(tmpAry.length)}`) * 3600
    }
    if (["5", "6", "7", "8", "9"].includes(tmpAry[1])) {
      return Number(`${(Number(tmpAry[0]) + 1).toString()}${"0".repeat(tmpAry.length - 1)}`) * 3600
    }
    if (["0", "1", "2", "3", "4"].includes(tmpAry[1])) {
      return Number(`${(Number(tmpAry[0])).toString()}5${"0".repeat(tmpAry.length - 2)}`) * 3600
    }
    return Number(`${tmpAry[0]}${(Number(tmpAry[1]) + 1).toString()}${"0".repeat(tmpAry.length - 2)}`) * 3600
  }, [list])

  const buildUnit = (value: number) => {
    if (!value) return <></>
    if (type === "person") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          人
        </>
      )
    }
    if (type === "biz") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          biz
        </>
      )
    }
    if (type === "page") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          枚
        </>
      )
    }
    if (type === "time") {
      return (
        <span>
          {secToHourMinute(value)}
        </span>
      )
    }
    if (type === "times") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          回
        </>
      )
    }
    if (type === "percent") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          %
        </>
      )
    }
    if (type === "hour") {
      return (
        <>
          <span>
            {Math.floor(value / 3600)}
          </span>
          時間
        </>
      )
    }
    if (type === "yen") {
      return (
        <>
          ¥
          <span>
            {value.toLocaleString()}
          </span>
        </>
      )
    }
    return (
      <></>
    )
  }

  const buildTip = (params: { name: string, value: number, color?: string }) => {
    let tmpVal = ""
    if (type === "person") {
      tmpVal = `${params.value.toLocaleString()}人`
    }
    if (type === "biz") {
      tmpVal = `${params.value.toLocaleString()}biz`
    }
    if (type === "page") {
      tmpVal = `${params.value.toLocaleString()}枚`
    }
    if (type === "time") {
      tmpVal = secToHourMinute(params.value)
    }
    if (type === "times") {
      tmpVal = `${params.value.toLocaleString()}回`
    }
    if (type === "percent") {
      tmpVal = `${params.value.toLocaleString()}%`
    }
    if (type === "hour") {
      tmpVal = secToHourMinute(params.value)
    }
    if (type === "yen") {
      tmpVal = `¥${params.value.toLocaleString()}`
    }
    return `${params.name} ${tmpVal}`
  }

  const buildLabel = (label: string) => {
    const text = format(label, "YYYYMM") === nowDate("YYYYMM") ? nowDate('YYYY年MM月DD日現在') : format(label, 'YYYY年MM月')
    return (
      <span>
        {text}
      </span>
    )
  }

  const buildHorizontalBarChart = () => (
    <>
      {
        list.map((value) => (
          <div
            className="row"
            style={{
              padding: gap ? "8px 0" : "0",
            }}
            key={`bar_chart_row_${value.label}`}
          >
            <div className="row_title">
              {buildLabel(value.label)}
            </div>
            <div className="row_value">
              <div
                className="stacked_bar_chart"
                style={{
                  width: `${(value.values.reduce((s, w) => s + w.value, 0) / (maxValue * 1.00)) * 100}%`,
                  backgroundColor: value.color || (value.values.reduce((s, w) => s + w.value, 0) === maxValue ? strongColor : color),
                }}
              >
                <div
                  className="label"
                  style={{
                    color: value.color || (value.values.reduce((s, w) => s + w.value, 0) === maxValue ? strongColor : color),
                  }}
                >
                  {buildUnit(value.values.reduce((s, w) => s + w.value, 0))}
                </div>
              </div>
            </div>
          </div>
        ))
      }
    </>
  )

  const buildVerticalBarChart = () => (
    <>
      {
        list.map((value, index) => (
          <div
            className="column_value"
            style={{
              height: `${chartSize}px`,
              width: `calc(100% / ${list.length || 1})`,
            }}
            key={uniqueId('column_value_')}
          >
            <div
              className="stacked_bar_chart"
              style={{
                width: "50%",
                height: `${(value.values.reduce((s, w) => s + w.value, 0) / (maxValue * 1.03)) * 100}%`,
                zIndex: 2,
              }}
            >
              {
                value.values.map((v) => (
                  <React.Fragment key={`${v.name}_${v.value}`}>
                    <div
                      className="bar"
                      style={{
                        height: `${(v.value / value.values.reduce((s, w) => s + w.value, 0)) * 100}%`,
                        background: `linear-gradient(to bottom, #FFFFFF 0.1px, ${v.color || "#EEEEEE"} 0.1px, ${v.color || "#EEEEEE"} 100%)`,
                        opacity: focusLabel && focusLabel !== v.name ? "0.2" : "1",
                      }}
                    >
                      {
                        tips ? (
                          <Tips
                            description={buildTip(v)}
                            className={focusLabel && focusLabel === v.name ? "lock" : ""}
                            icon={false}
                          >
                            <></>
                          </Tips>
                        ) : (
                          <></>
                        )
                      }
                    </div>
                  </React.Fragment>
                ))
              }
              {
                !ruler && type ? (
                  <div className="label">
                    {buildUnit(value.values.reduce((s, w) => s + w.value, 0))}
                  </div>
                ) : (
                  <></>
                )
              }
              <div
                style={{
                  position: "absolute",
                  top: "-50px",
                  textAlign: "center",
                  width: "100%",
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "#4D71B5",
                }}
              >
                <div>
                  合計時間
                </div>
                <div>
                  <span
                    style={{
                      fontSize: "20px",
                    }}
                  >
                    {secToHourMinute(value.values.reduce((s, v) => s + v.value, 0))}
                  </span>
                </div>
              </div>
            </div>
            {
              value.scheduled ? (
                <>
                  <div
                    className="stacked_bar_chart"
                    style={{
                      width: "50%",
                      height: `${((value.scheduled) / (maxValue * 1.03)) * 100}%`,
                      zIndex: 1,
                    }}
                  >
                    <div
                      className="bar"
                      style={{
                        height: "100%",
                        width: "100%",
                        backgroundColor: "#4d71b5",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: "-50px",
                        textAlign: "center",
                        width: "100%",
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: "#4D71B5",
                      }}
                    >
                      <div>
                        予定
                      </div>
                      <div>
                        <span
                          style={{
                            fontSize: "20px",
                          }}
                        >
                          {secToHourMinute(value.scheduled).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <></>
              )
            }
            {
              ruler ? (
                <div className="ruler_border" />
              ) : (
                <></>
              )
            }
            {
              labelSkip && (index % labelSkip) !== 0 ? (
                <></>
              ) : (
                <div className="column_title">
                  {buildLabel(value.label)}
                  <button
                    type="button"
                    onClick={() => clickCallback(value.label)}
                  >
                    {selectLabel !== value.label ? "選択" : "選択解除"}
                  </button>
                </div>
              )
            }
            {
              selectLabel === value.label && (
                <div className="selected" />
              )
            }
          </div>
        ))
      }
    </>
  )

  return (
    <>
      <div className="bar_chart_wrap" style={{ position: "relative" }}>
        {
          legend && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                flexWrap: "wrap",
                width: "100%",
                marginBottom: "80px",
              }}
            >
              {
                Array.from(new Map(list.map((v) => v.values).flat().map((v) => [v.name, v])).values()).map((v) => (
                  <React.Fragment key={`${v.name}_${v.value}`}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        cursor: "pointer",
                        opacity: focusLabel && focusLabel !== v.name ? "0.2" : "1",
                      }}
                      onMouseOver={() => setFocusLabel(v.name)}
                      onFocus={() => { /* */ }}
                      onMouseOut={() => setFocusLabel(null)}
                      onBlur={() => { /* */ }}
                    >
                      <div
                        style={{
                          backgroundColor: v.color,
                          width: "12px",
                          height: "12px",
                          borderRadius: round ? "50%" : "0",
                        }}
                      />
                      <div>
                        {v.name}
                      </div>
                    </div>
                  </React.Fragment>
                ))
              }
            </div>
          )
        }
        {
          horizontal ? (
            <div className={`horizontal ${(ruler && type) ? "ruler" : ""}`}>
              <div className="bar_chart_area">
                {
                  isGraphLoading && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexFlow: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        zIndex: 2,
                      }}
                    >
                      <LoadingSquare>
                        <>
                          表示するデータを生成しています…
                        </>
                      </LoadingSquare>
                    </div>
                  )
                }
                {
                  scale && Array((vLabelLimit || V_LABEL_LIMIT)).fill(null).map((_v, i) => (
                    <div
                      className="scale"
                      style={{
                        top: `calc((${chartSize}px / ${vLabelLimit || V_LABEL_LIMIT} * ${i}) + 8px)`,
                      }}
                      key={uniqueId("ruler")}
                    />
                  ))
                }
                {buildHorizontalBarChart()}
              </div>
            </div>
          ) : (
            <div className={`vertical ${(ruler && type) ? "ruler" : ""}`}>
              <div
                className="bar_chart_area"
                style={{
                  height: `${chartSize}px`,
                  justifyContent: gap ? "space-evenly" : "center",
                }}
              >
                {
                  isGraphLoading && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexFlow: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 2,
                        textShadow: "1px 1px 1px #ffffff",
                      }}
                    >
                      <LoadingSquare>
                        <>
                          表示するデータを生成しています…
                        </>
                      </LoadingSquare>
                    </div>
                  )
                }
                {
                  scale && Array((vLabelLimit || V_LABEL_LIMIT)).fill(null).map((_v, i) => (
                    <div
                      className="scale"
                      style={{
                        top: `calc((${chartSize}px / ${vLabelLimit || V_LABEL_LIMIT} * ${i}) + 8px)`,
                      }}
                      key={uniqueId("ruler")}
                    />
                  ))
                }
                {buildVerticalBarChart()}
              </div>
              {
                ruler ? (
                  <div className="ruler_area">
                    <div
                      className="horizontal_ruler_area"
                      style={{
                        top: `${chartSize}px`,
                      }}
                    />
                    {
                      type ? (
                        <div className="vertical_ruler_area">
                          <div className="label_area">
                            {
                              Array((vLabelLimit || V_LABEL_LIMIT)).fill(null).map((_v, i) => (
                                <React.Fragment key={uniqueId("ruler")}>
                                  <div
                                    className="scale"
                                    style={{
                                      top: `calc((${chartSize}px / ${vLabelLimit || V_LABEL_LIMIT} * ${i}) + 8px)`,
                                    }}
                                  />
                                  <div
                                    className="label"
                                    style={{
                                      top: `calc((${chartSize}px / ${vLabelLimit || V_LABEL_LIMIT} * ${i}) + 8px)`,
                                      fontSize: i ? "14px" : "20px",
                                      color: i ? "" : "#a5a00e",
                                    }}
                                  >
                                    {buildUnit(Math.floor((maxValue / (vLabelLimit || V_LABEL_LIMIT)) * ((vLabelLimit || V_LABEL_LIMIT) - i)))}
                                  </div>
                                </React.Fragment>
                              ))
                            }
                          </div>
                        </div>
                      ) : (
                        <></>
                      )
                    }
                  </div>
                ) : (
                  <></>
                )
              }
            </div>
          )
        }
      </div>
    </>
  )
}

DetailScheduleChart.defaultProps = {
  color: "#666666",
  strongColor: "#000000",
  type: "",
  gap: true,
  horizontal: false,
  ruler: false,
  scale: false,
  labelSkip: 0,
  vLabelLimit: 3,
  legend: false,
  tips: false,
  selectLabel: "",
  round: false,
}

export default DetailScheduleChart
